import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  CardMedia,
  Input,
  Tabs,
  Box,
  Tab,
  SelectChangeEvent,
  MenuItem,
  Checkbox,
  ListItemText,
  Select,
  InputAdornment,
  IconButton,
  CircularProgress,
  Modal,
} from "@mui/material";
import OTPInput from "react-otp-input";

import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VerifiedIcon from "@mui/icons-material/Verified";
import React, { ChangeEvent, useState, useEffect } from "react";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import { useFormik } from "formik";
import * as Yup from "yup";
import "react-phone-input-2/lib/bootstrap.css";
import PhoneInput from "react-phone-input-2";
import { isValidPhoneNumber } from "libphonenumber-js";
import {
  useUpdateProfileMutation,
  useLazyMyProfileQuery,
  useSendOtpToVerifyMutation,
  useVerifyOtpToVerifyMutation,
} from "../../service/auth";
import { OTP_TYPE } from "../../utils/enums";
import { showError, showToast } from "../../constants/toasts";
import { UploadMedia, Uploadpdf } from "../../utils/mediaUpload";
import { useLazyGetCategoryQuery } from "../../service/main";
import { Category } from "../../types/General";
import { useImageVerification } from "../../hooks/imageVerification";
import { isValidInput, validateUAEIBAN } from "../../utils/validations";
import Loader from "../../constants/Loader";
import {
  useLazyGetPayoutAccountStatusQuery,
  useLazyGetPayoutOnboardingLinkQuery,
  useLazyGetPayoutRequestInformationQuery,
} from "../../service/earnings";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
console.log('build')
const CustomTabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
  </div>
);

const ManageProfile = () => {
  const getSafeUrl = (val: any) => {
    if (!val) return "";
    if (typeof val === "string") {
      if (val === "[object Object]") return "";
      return val;
    }
    if (typeof val === "object") {
      return val.url || val.document || val.image || val.fileUrl || val.link || "";
    }
    return String(val);
  };

  const navigate = useNavigate();
  const [valueTabs, setValueTabs] = useState<number>(0);
  const [openCategory, setOpenCategory] = useState(false);
  const { validateImage, loading: loadingImage } = useImageVerification();
  const [updateProfile, { isLoading: isUpdatingProfile }] =
    useUpdateProfileMutation();
  const [myProfile, { isLoading: isProfileLoading, isFetching: isProfileFetching }] = useLazyMyProfileQuery({});
  const [getCategory] = useLazyGetCategoryQuery();
  const [user, setUser] = useState<any>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [isImageProcessing, setIsImageProcessing] = useState<boolean>(false);

  const [openModal, setOpenModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(60);

  useEffect(() => {
    let interval: any;
    if (timer > 0 && openModal) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer, openModal]);
  const [verificationType, setVerificationType] = useState<
    "email" | "phone" | null
  >(null);
  const [sendOtp, { isLoading: isSendingOtp }] = useSendOtpToVerifyMutation();
  const [verifyOtp, { isLoading: isVerifyingOtp }] =
    useVerifyOtpToVerifyMutation();

  // Stripe Connect Payouts endpoints and state
  const [triggerGetStatus] = useLazyGetPayoutAccountStatusQuery();
  const [triggerGetOnboardingLink] = useLazyGetPayoutOnboardingLinkQuery();
  const [triggerGetRequestInfo] = useLazyGetPayoutRequestInformationQuery();

  const [payoutStatusInfo, setPayoutStatusInfo] = useState<any>(null);
  const [payoutRequestInfo, setPayoutRequestInfo] = useState<any>(null);
  const [isPayoutStatusLoading, setIsPayoutStatusLoading] = useState(false);
  const [isPayoutActionLoading, setIsPayoutActionLoading] = useState(false);

  const fetchPayoutStatus = async (showLoading = true) => {
    if (showLoading) setIsPayoutStatusLoading(true);
    try {
      const res = await triggerGetStatus(undefined, false).unwrap();
      if (res?.statusCode === 200 && res?.data) {
        setPayoutStatusInfo(res.data);
        const statusStr = (res.data.stripeConnectStatus || res.data.status || "").toLowerCase();
        if (
          statusStr.includes("action_required") ||
          statusStr.includes("information_required") ||
          statusStr.includes("additional_information") ||
          statusStr.includes("requirements_due")
        ) {
          try {
            const infoRes = await triggerGetRequestInfo(undefined, false).unwrap();
            if (infoRes?.statusCode === 200) {
              setPayoutRequestInfo(infoRes.data);
            }
          } catch (err: any) {
            console.error("Failed to fetch request information", err);
          }
        } else {
          setPayoutRequestInfo(null);
        }
      }
    } catch (err: any) {
      console.error("Failed to fetch payout account status", err);
      const errMsg = err?.data?.message || err?.message || "";
      if (errMsg === "STRIPE_CONNECT_ACCOUNT_NOT_FOUND" || err?.data?.statusCode === 400 || err?.status === 400) {
        setPayoutStatusInfo({ status: "initial" });
        setPayoutRequestInfo(null);
      } else {
        showError(errMsg || "Failed to load payout account status");
      }
    } finally {
      if (showLoading) setIsPayoutStatusLoading(false);
    }
  };

  const handlePayoutAction = async () => {
    if (isPayoutActionLoading) return;
    setIsPayoutActionLoading(true);
    try {
      const redirectUrl = window.location.origin + "/manage-profile";
      const res = await triggerGetOnboardingLink({ url: redirectUrl }, false).unwrap();
      const url = res?.data?.onboardingUrl || res?.data?.url || (typeof res?.data === "string" ? res.data : null);
      if (res?.statusCode === 200 && url) {
        window.location.href = url;
      } else {
        showError(res?.message || "Failed to retrieve onboarding link");
      }
    } catch (err: any) {
      console.error("Failed to fetch onboarding link", err);
      showError(err?.data?.message || "Failed to generate Stripe onboarding link");
    } finally {
      setIsPayoutActionLoading(false);
    }
  };

  const getPayoutStatusDetails = () => {
    if (!payoutStatusInfo) {
      return {
        label: "Loading...",
        badgeBg: "#F2F2F7",
        badgeColor: "#8E8E93",
        message: "",
        showButton: false,
        buttonLabel: "",
        actionType: "none",
      };
    }

    const rawStatus = payoutStatusInfo.stripeConnectStatus || payoutStatusInfo.status || "";
    const status = rawStatus.toLowerCase();

    const mappings: Record<string, {
      label: string;
      badgeBg: string;
      badgeColor: string;
      message: string;
      showButton: boolean;
      buttonLabel: string;
      actionType: "onboarding" | "none";
    }> = {
      completed: {
        label: "Completed",
        badgeBg: "#E8F5E9",
        badgeColor: "#2E7D32",
        message: "Payout account connected successfully.",
        showButton: true,
        buttonLabel: "Configure",
        actionType: "none",
      },
      active: {
        label: "Completed",
        badgeBg: "#E8F5E9",
        badgeColor: "#2E7D32",
        message: "Payout account connected successfully.",
        showButton: true,
        buttonLabel: "Configure",
        actionType: "none",
      },
      verified: {
        label: "Completed",
        badgeBg: "#E8F5E9",
        badgeColor: "#2E7D32",
        message: "Payout account connected successfully.",
        showButton: true,
        buttonLabel: "Configure",
        actionType: "none",
      },
      pending: {
        label: "Verification Pending",
        badgeBg: "#FFF3E0",
        badgeColor: "#E65100",
        message: "Your payout account is under review.",
        showButton: false,
        buttonLabel: "",
        actionType: "none",
      },
      verification_pending: {
        label: "Verification Pending",
        badgeBg: "#FFF3E0",
        badgeColor: "#E65100",
        message: "Your payout account is under review.",
        showButton: false,
        buttonLabel: "",
        actionType: "none",
      },
      under_review: {
        label: "Verification Pending",
        badgeBg: "#FFF3E0",
        badgeColor: "#E65100",
        message: "Your payout account is under review.",
        showButton: false,
        buttonLabel: "",
        actionType: "none",
      },
      in_review: {
        label: "Verification Pending",
        badgeBg: "#FFF3E0",
        badgeColor: "#E65100",
        message: "Your payout account is under review.",
        showButton: false,
        buttonLabel: "",
        actionType: "none",
      },
      action_required: {
        label: "Additional Information Required",
        badgeBg: "#FFEBEE",
        badgeColor: "#C62828",
        message: "Stripe requires more information before payouts can be activated.",
        showButton: true,
        buttonLabel: "Complete Verification",
        actionType: "onboarding",
      },
      information_required: {
        label: "Additional Information Required",
        badgeBg: "#FFEBEE",
        badgeColor: "#C62828",
        message: "Stripe requires more information before payouts can be activated.",
        showButton: true,
        buttonLabel: "Complete Verification",
        actionType: "onboarding",
      },
      additional_information_required: {
        label: "Additional Information Required",
        badgeBg: "#FFEBEE",
        badgeColor: "#C62828",
        message: "Stripe requires more information before payouts can be activated.",
        showButton: true,
        buttonLabel: "Complete Verification",
        actionType: "onboarding",
      },
      requirements_due: {
        label: "Additional Information Required",
        badgeBg: "#FFEBEE",
        badgeColor: "#C62828",
        message: "Stripe requires more information before payouts can be activated.",
        showButton: true,
        buttonLabel: "Complete Verification",
        actionType: "onboarding",
      },
      initial: {
        label: "Initial",
        badgeBg: "#ECEFF1",
        badgeColor: "#455A64",
        message: "Payouts are securely managed through Stripe.",
        showButton: true,
        buttonLabel: "Setup Payout Account",
        actionType: "onboarding",
      },
      not_started: {
        label: "Initial",
        badgeBg: "#ECEFF1",
        badgeColor: "#455A64",
        message: "Payouts are securely managed through Stripe.",
        showButton: true,
        buttonLabel: "Setup Payout Account",
        actionType: "onboarding",
      },
      not_created: {
        label: "Initial",
        badgeBg: "#ECEFF1",
        badgeColor: "#455A64",
        message: "Payouts are securely managed through Stripe.",
        showButton: true,
        buttonLabel: "Setup Payout Account",
        actionType: "onboarding",
      },
    };

    const fallback = {
      label: rawStatus || "Initial",
      badgeBg: "#ECEFF1",
      badgeColor: "#455A64",
      message: "Payouts are securely managed through Stripe.",
      showButton: true,
      buttonLabel: "Setup Payout Account",
      actionType: "onboarding" as const,
    };

    const mapped = mappings[status] || fallback;

    let finalMessage = mapped.message;
    if (
      payoutRequestInfo?.message &&
      (status.includes("action") || status.includes("information") || status.includes("due"))
    ) {
      finalMessage = payoutRequestInfo.message;
    }

    return {
      label: mapped.label,
      badgeBg: mapped.badgeBg,
      badgeColor: mapped.badgeColor,
      message: finalMessage,
      showButton: mapped.showButton,
      buttonLabel: mapped.buttonLabel,
      actionType: mapped.actionType,
    };
  };

  useEffect(() => {
    if (valueTabs === 2) {
      fetchPayoutStatus(true);
    }
  }, [valueTabs]);

  useEffect(() => {
    const handleFocus = () => {
      if (valueTabs === 2) {
        fetchPayoutStatus(false);
      }
    };
    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [valueTabs]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && valueTabs === 2) {
        fetchPayoutStatus(false);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [valueTabs]);

  const [isEmailVerifiedLocally, setIsEmailVerifiedLocally] = useState(true);
  const [isPhoneVerifiedLocally, setIsPhoneVerifiedLocally] = useState(true);
  const [verifiedEmails, setVerifiedEmails] = useState<string[]>([]);
  const [verifiedPhones, setVerifiedPhones] = useState<string[]>([]);
  const [countryIso, setCountryIso] = useState("ae"); // ISO code for flag display
  const [dialCode, setDialCode] = useState("971"); // numeric dial code for API
  const dialCodeRef = React.useRef("971");
  const [phoneLength, setPhoneLength] = useState(12); // total digits (dialCode + local)
  const getExpectedLocalDigits = () => Math.max(1, phoneLength - dialCode.length);

  const syncUserState = (data: any) => {
    setUser(data);
    setIsEmailVerifiedLocally(data?.user?.isEmailVerified || false);
    setIsPhoneVerifiedLocally(data?.user?.isPhoneVerified || false);

    if (data?.user?.isEmailVerified && data?.user?.email) {
      setVerifiedEmails((prev) => Array.from(new Set([...prev, data.user.email])));
    }

    if (data?.user?.isPhoneVerified && data?.user?.phone) {
      const cleanStoredPhone = (data.user.phone || "").replace(/\D/g, "");
      const cleanStoredCode = (data.user.countryCode || "").replace(/\D/g, "");
      setVerifiedPhones((prev) => Array.from(new Set([...prev, cleanStoredCode + cleanStoredPhone])));
    }

    const storedCC = data?.user?.countryCode?.replace("+", "") || "971";
    setDialCode(storedCC);
    dialCodeRef.current = storedCC;

    const dialToIso: Record<string, string> = {
      "971": "ae", "91": "in", "1": "us", "44": "gb",
      "86": "cn", "92": "pk", "966": "sa", "974": "qa",
      "968": "om", "965": "kw", "973": "bh", "20": "eg",
    };
    setCountryIso(dialToIso[storedCC] || "ae");

    if (data?.user?.phone) {
      const fullPhone = storedCC + data.user.phone;
      setPhoneLength(fullPhone.length);
    } else {
      let localDots = 9;
      if (storedCC === "91" || storedCC === "1") localDots = 10;
      setPhoneLength(storedCC.length + localDots);
    }
  };

  const initialPhoneValue = React.useMemo(() => {
    if (!user) return null;
    const code = (user?.user?.countryCode || "").replace(/\D/g, "");
    let phoneStr = (user?.user?.phone || "").replace(/\D/g, "");
    if (code && phoneStr.startsWith(code) && phoneStr.length > code.length + 5) {
      phoneStr = phoneStr.slice(code.length);
    }
    return code ? code + phoneStr : phoneStr;
  }, [user]);


  const handleSendVerificationOtp = async (target: "email" | "phone") => {
    try {
      setVerificationType(target);
      const payload: any = {
        otpType: OTP_TYPE.UPDATE,
      };
      if (target === "email") {
        payload.email = formik.values.email;
      } else {
        const cleanPhone = formik.values.phone.replace("+", "");
        const localPart = cleanPhone.startsWith(dialCode)
          ? cleanPhone.slice(dialCode.length)
          : cleanPhone;

        // Validate before sending OTP
        const validateVal = "+" + cleanPhone;
        let isValid = false;
        try {
          isValid = isValidPhoneNumber(validateVal);
        } catch (error) {
          isValid = false;
        }

        if (!isValid) {
          showError(`Invalid phone number for the selected country`);
          formik.setFieldTouched("phone", true, true);
          return;
        }

        payload.phone = localPart;
        payload.countryCode = "+" + dialCode;
      }

      const response = await sendOtp(payload).unwrap();
      if (response.statusCode === 200 || response.statusCode === 201) {
        setTimer(60);
        setOpenModal(true);
        showToast("OTP sent successfully");
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to send OTP");
    }
  };

  const handleVerifyOtp = async () => {
    try {
      const payload: any = {
        otp,
        otpType: OTP_TYPE.UPDATE,
      };
      if (verificationType === "email") {
        payload.email = formik.values.email;
      } else {
        const cleanPhone = formik.values.phone.replace("+", "");
        payload.phone = cleanPhone.startsWith(dialCode)
          ? cleanPhone.slice(dialCode.length)
          : cleanPhone;
        payload.countryCode = "+" + dialCode;
      }

      const response = await verifyOtp(payload).unwrap();
      if (response.statusCode === 200 || response.statusCode === 201) {
        setOpenModal(false);
        setOtp("");
        if (verificationType === "email") {
          setIsEmailVerifiedLocally(true);
          setVerifiedEmails((prev) => Array.from(new Set([...prev, formik.values.email])));
        } else {
          setIsPhoneVerifiedLocally(true);
          const cleanPhoneVal = (formik.values.phone || "").replace(/\D/g, "");
          setVerifiedPhones((prev) => Array.from(new Set([...prev, cleanPhoneVal])));
        }
        showToast(
          `${verificationType === "email" ? "Email" : "Phone"} verified successfully`
        );
      } else {
        showError(response.message || "Invalid OTP");
        setOtp("");
      }
    } catch (error: any) {
      setOtp("");
      showError(error?.data?.message || "Invalid OTP");
    }
  };



  const handleImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (loading || loadingImage || isImageProcessing) return;

    const file = files[0];

    // ✅ mark touched
    formik.setFieldTouched("logoImage", true);
    formik.setFieldError("logoImage", "");

    // ✅ quick validations
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_FILE_SIZE) {
      formik.setFieldError("logoImage", "File size exceeds the allowed limit");
      setImagePreview("");
      showError("File size exceeds the allowed limit");
      return;
    }

    if (!file.type.startsWith("image/")) {
      formik.setFieldError("logoImage", "Unsupported file format");
      setImagePreview("");
      showError("Unsupported image format.");
      return;
    }

    setIsImageProcessing(true);
    try {
      // ✅ Step 1: Validate image (blur + NSFW)
      const result = await validateImage(file);

      if (!result.valid) {
        formik.setFieldError("logoImage", result.error);
        setImagePreview(""); // clear preview if invalid
        showError(result.error);
        return;
      }

      // ✅ Step 2: Show local preview
      setImagePreview(result.url);

      // ✅ Step 3: Upload
      const uploadRes = await UploadMedia(file);
      const uploadedImageUrl =
        (uploadRes as any)?.data?.imageUrl ||
        (uploadRes as any)?.data?.image ||
        (uploadRes as any)?.data?.url ||
        (typeof (uploadRes as any)?.data === "string"
          ? (uploadRes as any)?.data
          : "") ||
        (uploadRes as any)?.url;

      if (uploadedImageUrl) {
        formik.setFieldValue("logoImage", uploadedImageUrl, true);
        formik.setFieldTouched("logoImage", true, false);
        showToast("Image uploaded successfully.");
      } else {
        showError("Upload failed due to a network/server error");
      }
    } catch (err) {
      showError("Upload failed due to a network/server error");
    } finally {
      setIsImageProcessing(false);
    }
  };


  const step1Schema = Yup.object().shape({
    fullName: Yup.string()
      .trim()
      .required("Full Name is required")
      .min(2, "Must be at least 2 characters")
      .max(50, "Must not exceed 50 characters")
      .matches(/^[a-zA-Z\s]*$/, "Only alphabets are allowed"),
    businessName: Yup.string()
      .trim()
      .required("Business Name is required")
      .min(2, "Must be at least 2 characters")
      .max(100, "Must not exceed 100 characters"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phone: Yup.string()
      .required("Phone number is required")
      .test("is-valid-phone", "Invalid phone number for the selected country", (val, context) => {
        if (!val) return context.createError({ message: "Phone number is required" });
        const cleanVal = val.replace(/\D/g, "");
        if (cleanVal === dialCodeRef.current || cleanVal === "") {
          return context.createError({ message: "Please enter a valid phone number for selected country" });
        }
        try {
          if (!isValidPhoneNumber("+" + cleanVal)) {
            return context.createError({ message: "Invalid phone number for the selected country" });
          }
          return true;
        } catch (error) {
          return context.createError({ message: "Invalid phone number for the selected country" });
        }
      }),
    coverageArea: Yup.string()
      .required("Coverage Area is required")
      .notOneOf(["default"], "Coverage Area is required"),
    description: Yup.string().trim().max(250, "Max 250 characters"),
    category: Yup.array().min(1, "Category is required").required("Category is required"),
    logoImage: Yup.string().required("Logo is required"),
  });

  const step2Schema = Yup.object().shape({
    addressDetails: Yup.string()
      .trim()
      .required("Address details are required")
      .min(3, "Must be at least 3 characters")
      .max(200, "Must not exceed 200 characters"),
    emirate: Yup.string()
      .required("Emirate is required")
      .notOneOf(["default"], "Emirate is required"),
    city: Yup.string()
      .trim()
      .required("City is required")
      .min(2, "Must be at least 2 characters")
      .max(50, "Must not exceed 50 characters")
      .matches(/^[a-zA-Z\s]*$/, "Only alphabets are allowed"),
    streetName: Yup.string()
      .trim()
      .required("Street name is required")
      .min(2, "Must be at least 2 characters")
      .max(100, "Must not exceed 100 characters"),
    buildingName: Yup.string()
      .trim()
      .required("Building name is required")
      .min(1, "Must be at least 1 character")
      .max(100, "Must not exceed 100 characters"),
  });

  const step3Schema = Yup.object().shape({
    bankAccountName: Yup.string()
      .trim()
      .required("Bank Account Name is required")
      .min(2, "Must be at least 2 characters")
      .max(100, "Must not exceed 100 characters")
      .matches(
        /^[a-zA-Z0-9]+( [a-zA-Z0-9]+)*$/,
        "Cannot contain leading, trailing, or multiple spaces, and special characters are not allowed"
      ),
    bankName: Yup.string()
      .trim()
      .required("Bank Name is required")
      .min(2, "Must be at least 2 characters")
      .max(100, "Must not exceed 100 characters")
      .matches(
        /^[a-zA-Z0-9 ]*$/,
        "Special characters are not allowed in Bank Name"
      ),
    iban: Yup.string()
      .trim()
      .required("IBAN is required")
      .test("is-valid-uae-iban", function (value) {
        if (!value) return false;
        const result = validateUAEIBAN(value, false);
        if (!result.valid) {
          return this.createError({ message: result.error });
        }
        return true;
      }),
    swiftCode: Yup.string()
      .trim()
      .notRequired()
      .test("len", "Must be between 8 and 11 characters", (val) => !val || (val.length >= 8 && val.length <= 11))
      .matches(
        /^[a-zA-Z0-9]*$/,
        "Special characters are not allowed in Swift Code"
      ),
    bankDocument: Yup.string().notRequired(),
  });

  const step4Schema = Yup.object().shape({
    document1: Yup.mixed().required("Document 1 is required"),
    document2: Yup.mixed().notRequired(),
    document3: Yup.mixed().notRequired(),
  });

  const loadInitialData = async () => {
    setIsInitializing(true);
    try {
      const [profileRes, categoryRes] = await Promise.allSettled([
        myProfile({}).unwrap(),
        getCategory({}).unwrap()
      ]);

      if (profileRes.status === "fulfilled" && profileRes.value.statusCode === 200) {
        syncUserState(profileRes.value.data);
      }
      if (categoryRes.status === "fulfilled" && categoryRes.value.statusCode === 200) {
        setCategories(
          categoryRes.value.data?.data?.filter(
            (cat: any) => !cat.isBlocked && !cat.isDeleted
          ) || []
        );
      }
    } catch (e) {
      console.log(e);
    } finally {
      setIsInitializing(false);
    }
  };

  const myProfileApi = async () => {
    try {
      const response = await myProfile({}).unwrap();
      if (response.statusCode === 200) {
        syncUserState(response.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const formik = useFormik({
    initialValues: {
      fullName: user?.sp?.name || "",
      businessName: user?.sp?.businessName || "",
      email: user?.user?.email || "",
      phone: (() => {
        const code = (user?.user?.countryCode || "").replace(/\D/g, "");
        let phoneStr = (user?.user?.phone || "").replace(/\D/g, "");
        if (code && phoneStr.startsWith(code) && phoneStr.length > code.length + 5) {
          phoneStr = phoneStr.slice(code.length);
        }
        return code ? code + phoneStr : phoneStr;
      })(),
      coverageArea: user?.sp?.serviceArea[0] || "",
      description: user?.sp?.description || "",
      category:
        user?.sp?.categories?.map((item: any) => String(item.categoryId)) || [],
      logoImage: user?.sp?.logo || "",
      addressDetails: user?.sp?.address?.detail || "",
      emirate: user?.sp?.address?.emirate || "",
      city: user?.sp?.address?.city || "",
      streetName: user?.sp?.address?.streetName || "",
      buildingName: user?.sp?.address?.buildingName || "",
      bankAccountName: user?.sp?.bankDetails?.accountName || "",
      bankName: user?.sp?.bankDetails?.bankName || "",
      iban: user?.sp?.bankDetails?.iban || "",
      swiftCode: user?.sp?.bankDetails?.swift || "",
      bankDocument: user?.sp?.bankDetails?.document || "",
      document1: getSafeUrl(user?.sp?.additionalDocuments?.[0]),
      document2: getSafeUrl(user?.sp?.additionalDocuments?.[1]),
      document3: getSafeUrl(user?.sp?.additionalDocuments?.[2]),
    },
    enableReinitialize: true,
    validate: (values) => {
      const activeSchema =
        valueTabs === 0
          ? step1Schema
          : valueTabs === 1
            ? step2Schema
            : valueTabs === 2
              ? step3Schema
              : step4Schema;
      try {
        activeSchema.validateSync(values, { abortEarly: false });
        return {};
      } catch (err: any) {
        const errors: any = {};
        if (err.inner) {
          err.inner.forEach((validationError: any) => {
            errors[validationError.path] = validationError.message;
          });
        }
        return errors;
      }
    },
    onSubmit: async (values) => {
      if (valueTabs === 0) {
        const cleanStoredPhone = (user?.user?.phone || "").replace(/\D/g, "");
        const cleanStoredCode = (user?.user?.countryCode || "").replace(/\D/g, "");
        const storedFullPhone = cleanStoredCode + cleanStoredPhone;
        const cleanValuesPhone = (values.phone || "").replace(/\D/g, "");
        if (values.email !== user?.user?.email && !isEmailVerifiedLocally) {
          showError("Please verify your new email first");
          return;
        }
        if (cleanValuesPhone !== storedFullPhone && !isPhoneVerifiedLocally) {
          showError("Please verify your new phone number first");
          return;
        }
      }
      try {
        let payload: any = {};

        if (valueTabs === 0) {
          payload = {
            name: values.fullName,
            businessName: values.businessName,
            email: values.email,
            phone: values.phone.startsWith(dialCode)
              ? values.phone.slice(dialCode.length)
              : values.phone,
            countryCode: "+" + dialCode,
            description: values.description,
            category: values.category,
            serviceArea: [values.coverageArea],
            ...(values.logoImage && { logo: values.logoImage }),
          };
        } else if (valueTabs === 1) {
          payload = {
            address: {
              detail: values.addressDetails,
              emirate: values.emirate,
              city: values.city,
              streetName: values.streetName,
              buildingName: values.buildingName,
            },
          };
        } else if (valueTabs === 2) {
          const checksumCheck = validateUAEIBAN(values.iban, true);
          if (!checksumCheck.valid && checksumCheck.error === "Please enter a valid IBAN.") {
            formik.setFieldError("iban", checksumCheck.error);
            showError(checksumCheck.error);
            return;
          }
          payload = {
            bankDetails: {
              accountName: values.bankAccountName,
              bankName: values.bankName,
              iban: values.iban.replace(/\s+/g, '').toUpperCase(),
              swift: values.swiftCode,
              document: values.bankDocument,
            },
          };
        } else if (valueTabs === 3) {
          payload = {
            name: values.fullName,
            step: 4,
            isProfileComplete: true,
            additionalDocuments: [values.document1, values.document2, values.document3].filter(
              Boolean
            ),
          };
        }

        const response = await updateProfile(payload).unwrap();
        if (response.statusCode === 200 || response.statusCode === 201) {
          myProfileApi();
          setIsEditing(false);
          showToast("Profile updated successfully");
        }
      } catch (error: any) {
        showError(error?.data?.message || "Failed to update profile");
      }
    },
  });

  const [phoneKey, setPhoneKey] = useState("default");

  useEffect(() => {
    if (user && formik.values.phone === initialPhoneValue) {
      setPhoneKey(initialPhoneValue || "synced");
    }
  }, [user, formik.values.phone, initialPhoneValue]);

  const handleProfileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      formik.setFieldValue("logoImage", "");
      return;
    }

    setLoading(true);
    const imageUrl = (await UploadMedia(file)) as any;
    setLoading(false);
    if (imageUrl?.data) {
      const url =
        imageUrl.data?.imageUrl ||
        imageUrl.data?.image ||
        imageUrl.data?.url ||
        (typeof imageUrl.data === "string" ? imageUrl.data : "");
      formik.setFieldValue("logoImage", url || "");
    }
  };

  const handleBankDocumentUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      formik.setFieldValue("bankDocument", "");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showError("File size exceeds the allowed limit");
      return;
    }

    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      showError("Unsupported file format");
      return;
    }

    setLoading(true);
    const imageUrl = (await Uploadpdf(file)) as any;
    setLoading(false);
    if (imageUrl?.data) {
      const url =
        imageUrl.data?.imageUrl ||
        imageUrl.data?.image ||
        imageUrl.data?.url ||
        (typeof imageUrl.data === "string" ? imageUrl.data : "");
      formik.setFieldValue("bankDocument", url || "");
      showToast("Document uploaded successfully.");
    } else {
      showError(imageUrl?.message || "Upload failed due to a network/server error");
    }
  };

  const handleAdditionalDocumentUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    fieldName: "document1" | "document2" | "document3"
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      formik.setFieldValue(fieldName, "");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showError("File size exceeds the allowed limit");
      return;
    }

    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      showError("Unsupported file format");
      return;
    }

    setLoading(true);
    const imageUrl = (await Uploadpdf(file)) as any;
    setLoading(false);
    if (imageUrl?.data) {
      const url =
        imageUrl.data?.imageUrl ||
        imageUrl.data?.image ||
        imageUrl.data?.url ||
        (typeof imageUrl.data === "string" ? imageUrl.data : "");
      formik.setFieldValue(fieldName, url || "");
      showToast("Document uploaded successfully.");
    } else {
      showError(imageUrl?.message || "Upload failed due to a network/server error");
    }
  };

  const handleSelectAll = (
    event: React.MouseEvent,
    allItems: string[],
    selectedItems: string[],
    setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    event.stopPropagation();
    if (selectedItems.length === allItems.length) setSelectedItems([]);
    else setSelectedItems(allItems);
  };

  const handleItemClick = (
    event: React.MouseEvent,
    value: string,
    selectedItems: string[],
    setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    event.stopPropagation();
    const selectedIndex = selectedItems.indexOf(value);
    let newSelected: string[] = [];

    if (selectedIndex === -1) newSelected = [...selectedItems, value];
    else newSelected = selectedItems.filter((v) => v !== value);

    setSelectedItems(newSelected);
  };

  const [selectField1, setSelectField1] = React.useState("default");
  const handleChange1 = (event: SelectChangeEvent) => {
    setSelectField1(event.target.value as string);
  };

  const a11yProps = (index: number) => ({
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  });

  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  return (
    <>
      <Loader isLoad={isProfileLoading || isProfileFetching || isInitializing} />
      <div className="main_title">
        <h1>Manage Profile</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Manage Profile
        </p>
      </div>

      <div className="cards view_page">
        <div className="table_header">
          <div className="left_s">
            <Box className="custom_tabs1" sx={{ width: "100%", mt: 2 }}>
              <Tabs
                value={valueTabs}
                onChange={handleChangeTabs}
                aria-label="view product tabs"
                className="custom_tabs_links tabs_img"
              >
                <Tab label="General Details" {...a11yProps(0)} />
                <Tab label="Address Details" {...a11yProps(1)} />
                <Tab
                  // icon={<InfoIcon />}
                  label="Bank Account Details"
                  {...a11yProps(2)}
                />
                <Tab label="Documents" {...a11yProps(3)} />
              </Tabs>
            </Box>
          </div>
          <div className="right_s">
            <Button
              className="btn btn_primary"
              onClick={() => {
                if (isEditing) {
                  formik.resetForm();
                  myProfileApi();
                }
                setIsEditing(!isEditing);
              }}
            >
              {isEditing ? (
                <>
                  <CloseIcon /> Cancel
                </>
              ) : (
                <>
                  <EditIcon /> Edit Profile
                </>
              )}
            </Button>
          </div>
        </div>
        <CustomTabPanel value={valueTabs} index={0}>
          <form className="form" onSubmit={formik.handleSubmit}>
            {/* IMAGE UPLOAD */}
            <div className="gap_p">
              <div className="control_group w_100">
                <label htmlFor="icon-button-file">Upload Logo <span style={{ color: "red" }}>*</span></label>
                {(formik.values.logoImage || imagePreview) ? (
                  <div className="upload_image br_none">
                    <div className="upload_image_holder">
                      <figure>
                        <CardMedia
                          component="img"
                          image={formik.values.logoImage || imagePreview}
                          alt="photo"
                        />
                        {(isImageProcessing || loadingImage) && (
                          <div className="image_loader_overlay" style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(255,255,255,0.7)',
                            borderRadius: '50%'
                          }}>
                            <CircularProgress size={24} />
                          </div>
                        )}
                      </figure>

                      {isEditing && !(isImageProcessing || loadingImage) && (
                        <CloseIcon
                          onClick={() => {
                            formik.setFieldValue("logoImage", "");
                            setImagePreview("");
                          }}
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  <label
                    className={`upload_image br_none ${!isEditing ? "disabled" : ""}`}
                    htmlFor={isEditing ? "icon-button-file" : ""}
                  >
                    {isEditing && (
                      <Input
                        sx={{ display: "none" }}
                        id="icon-button-file"
                        type="file"
                        inputProps={{
                          accept: "image/png,image/jpeg",
                        }}
                        onChange={(e: any) => {
                          handleImageChange(e);
                          e.target.value = null;
                        }}
                      />
                    )}
                    <span className="upload_image_holder">
                      <figure className="add_img">
                        <img
                          src="/static/images/user_placeholder.png"
                          alt="icon"
                        />
                        {isEditing && (
                          <span className="upload_img_icon">
                            <img
                              src="/static/images/edit2_icon.svg"
                              alt="icon"
                            />
                          </span>
                        )}
                        {(isImageProcessing || loadingImage) && (
                          <div className="image_loader_overlay" style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(255,255,255,0.5)',
                            borderRadius: '50%'
                          }}>
                            <CircularProgress size={24} />
                          </div>
                        )}
                      </figure>
                    </span>
                  </label>

                )}
                {formik.touched.logoImage && formik.errors.logoImage && (
                  <p className="err_txt">{formik.errors.logoImage as string}</p>
                )}

              </div>

              {/* FULL NAME */}
              <div className="control_group w_33">
                <label htmlFor="fullName">Full Name <span style={{ color: "red" }}>*</span></label>
                <TextField
                  id="fullName"
                  name="fullName"
                  placeholder="Enter Full Name"
                  fullWidth
                  value={formik.values.fullName || ""}
                  onChange={(e) => {
                    e.target.value = e.target.value.trimStart();
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  disabled={!isEditing}
                  error={
                    formik.touched.fullName && Boolean(formik.errors.fullName)
                  }
                  helperText={
                    formik.touched.fullName &&
                    (formik.errors.fullName as string)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </div>

              {/* BUSINESS NAME */}
              <div className="control_group w_33">
                <label htmlFor="businessName">Business Name <span style={{ color: "red" }}>*</span></label>
                <TextField
                  id="businessName"
                  name="businessName"
                  placeholder="Enter Business Name"
                  fullWidth
                  value={formik.values.businessName || ""}
                  onChange={(e) => {
                    e.target.value = e.target.value.trimStart();
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  disabled={!isEditing}
                  error={
                    formik.touched.businessName &&
                    Boolean(formik.errors.businessName)
                  }
                  helperText={
                    formik.touched.businessName &&
                    (formik.errors.businessName as string)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </div>

              {/* CONTACT EMAIL */}
              <div className="control_group w_33">
                <label htmlFor="email">Contact Email <span style={{ color: "red" }}>*</span></label>
                <TextField
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter Email Address"
                  fullWidth
                  disabled={!isEditing}
                  value={formik.values.email || ""}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={
                    formik.touched.email && (formik.errors.email as string)
                  }
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment style={{ paddingRight: "25px" }} position="end">
                        {isEmailVerifiedLocally && formik.values.email ? (
                          <VerifiedIcon color="success" />
                        ) : isEditing && !isEmailVerifiedLocally && formik.values.email ? (
                          <Button
                            size="small"
                            onClick={() => handleSendVerificationOtp("email")}
                            disabled={isSendingOtp}
                          >
                            {isSendingOtp ? "Sending..." : "Verify"}
                          </Button>
                        ) : null}
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    opacity: 0.6,
                    "& .MuiInputBase-input": {
                      cursor: "not-allowed",
                    }
                  }}
                  onChange={(e) => {
                    e.target.value = e.target.value.trimStart();
                    formik.handleChange(e);
                    if (verifiedEmails.includes(e.target.value)) {
                      setIsEmailVerifiedLocally(true);
                    } else if (e.target.value === user?.user?.email) {
                      setIsEmailVerifiedLocally(user?.user?.isEmailVerified);
                    } else {
                      setIsEmailVerifiedLocally(false);
                    }
                  }}
                />
              </div>

              {/* CONTACT PHONE */}
              <div
                className="control_group w_33"
                style={{ position: "relative" }}
              >
                <label htmlFor="phone">Contact Phone <span style={{ color: "red" }}>*</span></label>
                <div className="verify_input">
                  <PhoneInput
                    inputProps={{
                      id: "phone",
                      name: "phone",
                      placeholder: "Enter Phone Number",
                    }}
                    country={countryIso}
                    disableCountryGuess
                    autoFormat={false}
                    value={formik.values.phone}
                    onChange={(value, data: any) => {
                      const currentDialCode = data?.dialCode || dialCode;
                      const currentIso = data?.countryCode || countryIso;

                      let calculatedMaxLen = currentDialCode.length + 9; // fallback for UAE
                      if (data?.format) {
                        const dots = (data.format.match(/\./g) || []).length;
                        if (dots > 0) calculatedMaxLen = dots;
                      } else {
                        let localDots = 9;
                        if (currentDialCode === "91" || currentDialCode === "1") localDots = 10;
                        calculatedMaxLen = currentDialCode.length + localDots;
                      }

                      // Allow typing freely and let formik validation handle max length
                      const newPhoneValue = value;

                      formik.setFieldValue("phone", newPhoneValue);
                      formik.setFieldTouched("phone", true, false);

                      // Always sync state so getExpectedLocalDigits() is accurate
                      setDialCode(currentDialCode);
                      dialCodeRef.current = currentDialCode;
                      setCountryIso(currentIso);
                      setPhoneLength(calculatedMaxLen);
                      const cleanTrimmedValue = (newPhoneValue || "").replace(/\D/g, "");
                      if (verifiedPhones.includes(cleanTrimmedValue) && cleanTrimmedValue !== "") {
                        setIsPhoneVerifiedLocally(true);
                      } else {
                        setIsPhoneVerifiedLocally(false);
                      }
                    }}
                    onBlur={() => formik.setFieldTouched("phone", true)}
                    disabled={!isEditing}
                  />
                  <div className="eye_btn">
                    {(() => {
                      const phoneStr = String(formik.values.phone || "").replace(/\D/g, "");
                      if (phoneStr.length < 5) return null;

                      if (isPhoneVerifiedLocally) {
                        return <VerifiedIcon color="success" />;
                      }
                      if (isEditing && !isPhoneVerifiedLocally) {
                        return (
                          <Button
                            size="small"
                            onClick={() => handleSendVerificationOtp("phone")}
                            disabled={isSendingOtp}
                            sx={{ minWidth: "auto" }}
                          >
                            {isSendingOtp ? "Sending..." : "Verify"}
                          </Button>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  {formik.touched.phone && formik.errors.phone ? (
                    <p className="err_txt">{formik.errors.phone as string}</p>
                  ) : null}
                </div>
              </div>

              {/* SERVICE COVERAGE AREA (LOCATIONS) */}
              <div className="control_group w_33">
                <label htmlFor="coverageArea">Service Coverage Area (Locations) <span style={{ color: "red" }}>*</span></label>
                <Select
                  name="coverageArea"
                  fullWidth
                  value={formik.values.coverageArea || "default"}
                  onChange={formik.handleChange}
                  disabled={!isEditing}
                  inputProps={{ id: "coverageArea" }}
                >
                  <MenuItem disabled value="default">
                    Select
                  </MenuItem>
                  <MenuItem value="Abu Dhabi">Abu Dhabi</MenuItem>
                  <MenuItem value="Dubai">Dubai</MenuItem>
                  <MenuItem value="Sharjah">Sharjah</MenuItem>
                  <MenuItem value="Ajman">Ajman</MenuItem>
                  <MenuItem value="Umm al-Quwain">Umm al-Quwain</MenuItem>
                  <MenuItem value="Ras al-Khaimah">Ras al-Khaimah</MenuItem>
                  <MenuItem value="Fujairah">Fujairah</MenuItem>
                </Select>
              </div>

              {/* CATEGORY MULTI SELECT */}
              <div className="control_group w_33">
                <label htmlFor="category">Category <span style={{ color: "red" }}>*</span></label>
                <Select
                  multiple
                  displayEmpty
                  open={openCategory}
                  onOpen={() => isEditing && setOpenCategory(true)}
                  onClose={() => setOpenCategory(false)}
                  value={formik.values.category}
                  disabled={!isEditing}
                  onBlur={() => formik.setFieldTouched("category", true)}
                  renderValue={(selected) => {
                    if ((selected as string[]).length === 0) {
                      return (
                        <span style={{ color: "#999" }}>Select Category</span>
                      );
                    }
                    return (selected as string[])
                      .map(
                        (id) => categories.find((cat) => cat.id === id)?.name
                      )
                      .filter(Boolean)
                      .join(", ");
                  }}
                  fullWidth
                  MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                >
                  <MenuItem
                    value="all"
                    onClick={(e) =>
                      handleSelectAll(
                        e,
                        categories?.map((cat) => cat?.id),
                        formik.values.category,
                        (value) => formik.setFieldValue("category", value)
                      )
                    }
                  >
                    <Checkbox
                      checked={
                        categories.length > 0 &&
                        formik.values.category.length === categories.length
                      }
                      indeterminate={
                        formik.values.category.length > 0 &&
                        formik.values.category.length < categories.length
                      }
                    />
                    <ListItemText
                      primary={
                        formik.values.category.length === categories.length
                          ? "Clear All"
                          : "Select All"
                      }
                    />
                  </MenuItem>

                  {categories.map((cat) => (
                    <MenuItem
                      key={cat.id}
                      value={cat.id}
                      onClick={(e) =>
                        handleItemClick(
                          e,
                          cat.id,
                          formik.values.category,
                          (value) => formik.setFieldValue("category", value)
                        )
                      }
                    >
                      <Checkbox checked={formik.values.category.includes(cat.id)} />
                      <ListItemText primary={cat.name} />
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.category && formik.errors.category && (
                  <p className="err_txt">{formik.errors.category as string}</p>
                )}
              </div>

              {/* BUSINESS DESCRIPTION */}
              <div className="control_group w_100 ai_rec_btn">
                <label htmlFor="description">
                  Business Description
                  <p style={{ margin: 0 }}>(250 characters maximum)</p>
                </label>
                <TextField
                  id="description"
                  name="description"
                  placeholder="Enter Business Description"
                  multiline
                  fullWidth
                  value={formik.values.description || ""}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (!isValidInput(inputValue)) {
                      return;
                    }
                    e.target.value = e.target.value.trimStart();
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  disabled={!isEditing}
                  error={
                    formik.touched.description &&
                    Boolean(formik.errors.description)
                  }
                  helperText={
                    formik.touched.description &&
                    (formik.errors.description as string)
                  }
                  InputLabelProps={{ shrink: true }}
                />

              </div>
              {isEditing && (
                <div className="form_btn">
                  <Button
                    className="btn btn_primary"
                    variant="contained"
                    type="submit"
                    disabled={isUpdatingProfile}
                  >
                    {isUpdatingProfile ? (user?.sp ? "Updating..." : "Saving...") : (user?.sp ? "Update" : "Save")}
                  </Button>
                </div>
              )}
            </div>
          </form>
        </CustomTabPanel>

        <CustomTabPanel value={valueTabs} index={1}>
          <form className="form" onSubmit={formik.handleSubmit}>
            <div className="gap_p">
              {/* ADDRESS DETAILS */}
              <div className="control_group w_50">
                <label htmlFor="addressDetails">Address Details <span style={{ color: "red" }}>*</span></label>
                <TextField
                  id="addressDetails"
                  name="addressDetails"
                  placeholder="Enter Full Address Details (e.g. Area, Villa/Apt #)"
                  fullWidth
                  value={formik.values.addressDetails || ""}
                  onChange={(e) => {
                    if (!isValidInput(e.target.value)) {
                      return;
                    }
                    e.target.value = e.target.value.trimStart();
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  disabled={!isEditing}
                  error={
                    formik.touched.addressDetails &&
                    Boolean(formik.errors.addressDetails)
                  }
                  helperText={
                    formik.touched.addressDetails &&
                    (formik.errors.addressDetails as string)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </div>

              {/* EMIRATE */}
              <div className="control_group w_50">
                <label htmlFor="emirate">Emirate <span style={{ color: "red" }}>*</span></label>
                <Select
                  id="emirate"
                  name="emirate"
                  fullWidth
                  value={formik.values.emirate || "default"}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={!isEditing}
                  error={
                    formik.touched.emirate && Boolean(formik.errors.emirate)
                  }
                >
                  <MenuItem disabled value="default">
                    Select Emirate
                  </MenuItem>
                  <MenuItem value="Abu Dhabi">Abu Dhabi</MenuItem>
                  <MenuItem value="Dubai">Dubai</MenuItem>
                  <MenuItem value="Sharjah">Sharjah</MenuItem>
                  <MenuItem value="Ajman">Ajman</MenuItem>
                  <MenuItem value="Umm Al Quwain">Umm Al Quwain</MenuItem>
                  <MenuItem value="Ras Al Khaimah">Ras Al Khaimah</MenuItem>
                  <MenuItem value="Fujairah">Fujairah</MenuItem>
                </Select>
                {formik.touched.emirate && formik.errors.emirate && (
                  <p className="err_txt">{formik.errors.emirate as string}</p>
                )}
              </div>

              {/* CITY */}
              <div className="control_group w_50">
                <label htmlFor="city">City <span style={{ color: "red" }}>*</span></label>
                <TextField
                  id="city"
                  name="city"
                  placeholder="Enter City (e.g. Dubai)"
                  fullWidth
                  value={formik.values.city || ""}
                  onChange={(e) => {

                    if (!isValidInput(e.target.value)) {
                      return;
                    }
                    e.target.value = e.target.value.trimStart();
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  disabled={!isEditing}
                  error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={
                    formik.touched.city && (formik.errors.city as string)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </div>

              {/* STREET NAME */}
              <div className="control_group w_50">
                <label htmlFor="streetName">Street Name <span style={{ color: "red" }}>*</span></label>
                <TextField
                  id="streetName"
                  name="streetName"
                  placeholder="Enter Street Name (e.g. Al Maktoum St)"
                  fullWidth
                  value={formik.values.streetName || ""}
                  onChange={(e) => {

                    if (!isValidInput(e.target.value)) {
                      return;
                    }
                    e.target.value = e.target.value.trimStart();
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  disabled={!isEditing}
                  error={
                    formik.touched.streetName &&
                    Boolean(formik.errors.streetName)
                  }
                  helperText={
                    formik.touched.streetName &&
                    (formik.errors.streetName as string)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </div>

              {/* BUILDING NAME/NUMBER */}
              <div className="control_group w_50">
                <label htmlFor="buildingName">Building Name/Number <span style={{ color: "red" }}>*</span></label>
                <TextField
                  id="buildingName"
                  name="buildingName"
                  placeholder="Enter Building Name or Number (e.g. Burj Khalifa)"
                  fullWidth
                  value={formik.values.buildingName || ""}
                  onChange={(e) => {
                    if (!isValidInput(e.target.value)) {
                      return;
                    }
                    e.target.value = e.target.value.trimStart();
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  disabled={!isEditing}
                  error={
                    formik.touched.buildingName &&
                    Boolean(formik.errors.buildingName)
                  }
                  helperText={
                    formik.touched.buildingName &&
                    (formik.errors.buildingName as string)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </div>
              {isEditing && (
                <div className="form_btn">
                  <Button
                    className="btn btn_primary"
                    variant="contained"
                    type="submit"
                    disabled={isUpdatingProfile}
                  >
                    {isUpdatingProfile ? (user?.sp ? "Updating..." : "Saving...") : (user?.sp ? "Update" : "Save")}
                  </Button>
                </div>
              )}
            </div>
          </form>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={2}>
          <form
            className="form wrap_viewDemo_steps"
            onSubmit={formik.handleSubmit}
          >
            <div className="gap_p bank_details">
              {/* BANK ACCOUNT NAME */}
              <div className="control_group w_50">
                <label htmlFor="bankAccountName">
                  Bank Account Name <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  id="bankAccountName"
                  name="bankAccountName"
                  placeholder="Enter Bank Account Name"
                  fullWidth
                  value={formik.values.bankAccountName || ""}
                  onChange={(e) => {
                    const value = e.target.value.trimStart().replace(/[^a-zA-Z0-9 ]/g, "");
                    formik.setFieldValue("bankAccountName", value);
                  }}
                  onBlur={formik.handleBlur}
                  disabled={!isEditing}
                  InputLabelProps={{ shrink: true }}
                  error={
                    formik.touched.bankAccountName &&
                    Boolean(formik.errors.bankAccountName)
                  }
                  helperText={
                    formik.touched.bankAccountName &&
                    (formik.errors.bankAccountName as string)
                  }
                />
              </div>

              {/* BANK NAME */}
              <div className="control_group w_50">
                <label htmlFor="bankName">Bank Name <span style={{ color: "red" }}>*</span></label>
                <TextField
                  id="bankName"
                  name="bankName"
                  placeholder="Enter Bank Name"
                  fullWidth
                  value={formik.values.bankName || ""}
                  onChange={(e) => {
                    const value = e.target.value.trimStart().replace(/[^a-zA-Z0-9 ]/g, "");
                    formik.setFieldValue("bankName", value);
                  }}
                  onBlur={formik.handleBlur}
                  disabled={!isEditing}
                  error={
                    formik.touched.bankName && Boolean(formik.errors.bankName)
                  }
                  helperText={
                    formik.touched.bankName &&
                    (formik.errors.bankName as string)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </div>

              {/* IBAN */}
              <div className="control_group w_50">
                <label htmlFor="iban">IBAN <span style={{ color: "red" }}>*</span></label>
                <TextField
                  id="iban"
                  name="iban"
                  placeholder="Enter IBAN Number"
                  fullWidth
                  value={formik.values.iban || ""}
                  onChange={(e) => {
                    const value = e.target.value.trimStart().replace(/[^a-zA-Z0-9 ]/g, "");
                    formik.setFieldValue("iban", value);
                  }}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    const val = e.target.value;
                    const res = validateUAEIBAN(val, true);
                    if (!res.valid && res.error === "Please enter a valid IBAN.") {
                      setTimeout(() => formik.setFieldError("iban", res.error), 0);
                    }
                  }}
                  disabled={!isEditing}
                  error={formik.touched.iban && Boolean(formik.errors.iban)}
                  helperText={
                    formik.touched.iban && (formik.errors.iban as string)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </div>

              {/* SWIFT / BIC CODE */}
              <div className="control_group w_50">
                <label htmlFor="swiftCode">Swift Code</label>
                <TextField
                  id="swiftCode"
                  name="swiftCode"
                  placeholder="Enter Swift/BIC Code"
                  fullWidth
                  value={formik.values.swiftCode || ""}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
                    formik.setFieldValue("swiftCode", value);
                  }}
                  onBlur={formik.handleBlur}
                  disabled={!isEditing}
                  error={
                    formik.touched.swiftCode && Boolean(formik.errors.swiftCode)
                  }
                  helperText={
                    formik.touched.swiftCode &&
                    (formik.errors.swiftCode as string)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </div>

              {/* BANK DOCUMENT UPLOAD */}
              <div className="control_group w_100">
                <label htmlFor="bankDocument">
                  Bank Document Upload
                </label>
                <div className="upload_box">
                  {formik.values.bankDocument ? (
                    <div
                      className="uploaded_file"
                      style={{
                        position: "relative",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "150px",
                        border: "1px dashed #D3B88C",
                        borderRadius: "8px",
                      }}
                    >
                      <a
                        href={getSafeUrl(formik.values.bankDocument)}
                        target="_blank"
                        rel="noreferrer"
                        className="file_info"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "8px",
                          width: "100%",
                          textDecoration: "none",
                          color: "inherit",
                          cursor: "pointer",
                        }}
                      >
                        {getSafeUrl(formik.values.bankDocument)
                          .toLowerCase()
                          .match(/\.(pdf|doc|docx)$/) ? (
                          <>
                            <PictureAsPdfIcon
                              sx={{ color: "#D3B88C", fontSize: "30px" }}
                            />
                            <span style={{ fontSize: "12px", wordBreak: "break-all", textAlign: "center", padding: "0 10px" }}>
                              {getSafeUrl(formik.values.bankDocument).split('/').pop()?.split('?')[0]}
                            </span>
                          </>
                        ) : (
                          <img
                            src={getSafeUrl(formik.values.bankDocument)}
                            alt="preview"
                            style={{
                              width: "100%",
                              height: "auto",
                              maxHeight: "150px",
                              objectFit: "cover",
                              borderRadius: "4px",
                            }}
                          />
                        )}
                      </a>
                      {isEditing && (
                        <IconButton
                          size="small"
                          onClick={() =>
                            formik.setFieldValue("bankDocument", "")
                          }
                          sx={{ position: "absolute", top: 5, right: 5 }}
                        >
                          <CloseIcon color="error" />
                        </IconButton>
                      )}
                    </div>
                  ) : (
                    <label
                      className={`upload_action ${!isEditing ? "disabled" : ""}`}
                    >
                      {isEditing && (
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e: any) => {
                            handleBankDocumentUpload(e);
                            e.target.value = null;
                          }}
                          style={{ display: "none" }}
                        />
                      )}
                      <p>
                        <img
                          src="/static/images/upload_icon.svg"
                          alt="icon"
                        />
                        <strong>Click to upload or drag and drop</strong>
                      </p>
                    </label>
                  )}
                </div>
              </div>
              {isEditing && (
                <div className="form_btn">
                  <Button
                    className="btn btn_primary"
                    variant="contained"
                    type="submit"
                    disabled={isUpdatingProfile}
                  >
                    {isUpdatingProfile ? (user?.sp ? "Updating..." : "Saving...") : (user?.sp ? "Update" : "Save")}
                  </Button>
                </div>
              )}
            </div>
          </form>

          {/* Payout Account Settings section */}
          <div
            style={{
              marginTop: "24px",
              padding: "24px",
              borderRadius: "12px",
              border: "1px solid #ECEFF1",
              backgroundColor: "#FFFFFF",
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.04)",
              maxWidth: "100%",
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", marginBottom: "20px" }}>
              <div
                style={{
                  backgroundColor: "#F9F5F0",
                  borderRadius: "50%",
                  width: "48px",
                  height: "48px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D3B88C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="5" width="20" height="14" rx="2" ry="2"></rect>
                  <line x1="2" y1="10" x2="22" y2="10"></line>
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#1E1E1E",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  Payout Account Settings
                </h3>
                <p
                  style={{
                    margin: "4px 0 0 0",
                    fontSize: "13px",
                    color: "#717182",
                    lineHeight: "1.5",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  Bouteek partners with Stripe for secure payments and financial services.
                </p>
              </div>
            </div>

            {/* Status Display Box */}
            <div
              style={{
                backgroundColor: "#F8F9FA",
                borderRadius: "8px",
                padding: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "10px",
                marginBottom: "16px",
              }}
            >
              <span
                style={{
                  fontSize: "14px",
                  color: "#1E1E1E",
                  fontWeight: 500,
                  fontFamily: "Inter, sans-serif",
                }}
              >
                Payout Setup Status:
              </span>
              {isPayoutStatusLoading ? (
                <CircularProgress size={18} thickness={5} sx={{ color: "#D3B88C" }} />
              ) : (
                <span
                  style={{
                    backgroundColor: getPayoutStatusDetails().badgeBg,
                    color: getPayoutStatusDetails().badgeColor,
                    fontSize: "12px",
                    fontWeight: 600,
                    padding: "6px 14px",
                    borderRadius: "100px",
                    fontFamily: "Inter, sans-serif",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  {getPayoutStatusDetails().label}
                </span>
              )}
            </div>

            {/* Success/Information Message */}
            {!isPayoutStatusLoading && getPayoutStatusDetails().message && (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "8px",
                  marginBottom: "20px",
                }}
              >
                <InfoIcon
                  style={{
                    color: getPayoutStatusDetails().badgeColor,
                    fontSize: "18px",
                    marginTop: "1px",
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: "13px",
                    color: "#717182",
                    lineHeight: "1.4",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  {getPayoutStatusDetails().message}
                </span>
              </div>
            )}

            {/* CTA Button */}
            {!isPayoutStatusLoading && getPayoutStatusDetails().showButton && (
              <Button
                type="button"
                className="btn btn_primary"
                variant="contained"
                onClick={handlePayoutAction}
                disabled={isPayoutActionLoading}
                sx={{
                  width: "100%",
                  textTransform: "none",
                  height: "46px",
                  fontSize: "14px",
                  fontWeight: 600,
                  borderRadius: "8px",
                  fontFamily: "Inter, sans-serif",
                  backgroundColor: "#D3B88C",
                  "&:hover": {
                    backgroundColor: "#C2A77B",
                  },
                }}
              >
                {isPayoutActionLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  getPayoutStatusDetails().buttonLabel
                )}
              </Button>
            )}
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={3}>
          <form
            className="form wrap_viewDemo_steps"
            onSubmit={formik.handleSubmit}
          >
            <div className="gap_p bank_details">
              <div className="control_group w_50">
                <label htmlFor="document1">Upload Valid Trade License <span style={{ color: "red" }}>*</span></label>
                <div className="upload_box">
                  {formik.values.document1 ? (
                    <div
                      className="uploaded_file"
                      style={{
                        position: "relative",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "150px",
                        border: "1px dashed #D3B88C",
                        borderRadius: "8px",
                      }}
                    >
                      <a
                        href={getSafeUrl(formik.values.document1)}
                        target="_blank"
                        rel="noreferrer"
                        className="file_info"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "8px",
                          width: "100%",
                          textDecoration: "none",
                          color: "inherit",
                          cursor: "pointer",
                        }}
                      >
                        {getSafeUrl(formik.values.document1)
                          .toLowerCase()
                          .match(/\.(pdf|doc|docx)$/) ? (
                          <>
                            <PictureAsPdfIcon
                              sx={{ color: "#D3B88C", fontSize: "30px" }}
                            />
                            <span style={{ fontSize: "12px", wordBreak: "break-all", textAlign: "center", padding: "0 10px" }}>
                              {getSafeUrl(formik.values.document1).split('/').pop()?.split('?')[0]}
                            </span>
                          </>
                        ) : (
                          <img
                            src={getSafeUrl(formik.values.document1)}
                            alt="preview"
                            style={{
                              width: "100%",
                              height: "auto",
                              maxHeight: "150px",
                              objectFit: "cover",
                              borderRadius: "4px",
                            }}
                          />
                        )}
                      </a>
                      {isEditing && (
                        <IconButton
                          size="small"
                          onClick={() =>
                            formik.setFieldValue("document1", "")
                          }
                          sx={{ position: "absolute", top: 5, right: 5 }}
                        >
                          <CloseIcon color="error" />
                        </IconButton>
                      )}
                    </div>
                  ) : (
                    <label
                      className={`upload_action ${!isEditing ? "disabled" : ""}`}
                    >
                      {isEditing && (
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e: any) => {
                            handleAdditionalDocumentUpload(e, "document1");
                            e.target.value = null;
                          }}
                          style={{ display: "none" }}
                        />
                      )}
                      <p>
                        <img
                          src="/static/images/upload_icon.svg"
                          alt="icon"
                        />
                        <strong>Click to upload or drag and drop</strong>
                      </p>
                    </label>
                  )}
                </div>
                {formik.touched.document1 && formik.errors.document1 && (
                  <p className="err_txt">{formik.errors.document1 as string}</p>
                )}
              </div>
              <div className="control_group w_50">
                <label htmlFor="document2">Upload Commercial Registration Certificate <span style={{ color: "red" }}>*</span></label>
                <div className="upload_box">
                  {formik.values.document2 ? (
                    <div
                      className="uploaded_file"
                      style={{
                        position: "relative",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "150px",
                        border: "1px dashed #D3B88C",
                        borderRadius: "8px",
                      }}
                    >
                      <a
                        href={getSafeUrl(formik.values.document2)}
                        target="_blank"
                        rel="noreferrer"
                        className="file_info"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "8px",
                          width: "100%",
                          textDecoration: "none",
                          color: "inherit",
                          cursor: "pointer",
                        }}
                      >
                        {getSafeUrl(formik.values.document2)
                          .toLowerCase()
                          .match(/\.(pdf|doc|docx)$/) ? (
                          <>
                            <PictureAsPdfIcon
                              sx={{ color: "#D3B88C", fontSize: "30px" }}
                            />
                            <span style={{ fontSize: "12px", wordBreak: "break-all", textAlign: "center", padding: "0 10px" }}>
                              {getSafeUrl(formik.values.document2).split('/').pop()?.split('?')[0]}
                            </span>
                          </>
                        ) : (
                          <img
                            src={getSafeUrl(formik.values.document2)}
                            alt="preview"
                            style={{
                              width: "100%",
                              height: "auto",
                              maxHeight: "150px",
                              objectFit: "cover",
                              borderRadius: "4px",
                            }}
                          />
                        )}
                      </a>
                      {isEditing && (
                        <IconButton
                          size="small"
                          onClick={() =>
                            formik.setFieldValue("document2", "")
                          }
                          sx={{ position: "absolute", top: 5, right: 5 }}
                        >
                          <CloseIcon color="error" />
                        </IconButton>
                      )}
                    </div>
                  ) : (
                    <label
                      className={`upload_action ${!isEditing ? "disabled" : ""}`}
                    >
                      {isEditing && (
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e: any) => {
                            handleAdditionalDocumentUpload(e, "document2");
                            e.target.value = null;
                          }}
                          style={{ display: "none" }}
                        />
                      )}
                      <p>
                        <img
                          src="/static/images/upload_icon.svg"
                          alt="icon"
                        />
                        <strong>Click to upload or drag and drop</strong>
                      </p>
                    </label>
                  )}
                </div>
              </div>
              <div className="control_group w_50">
                <label htmlFor="document3">Upload Vat Certificate</label>
                <div className="upload_box">
                  {formik.values.document3 ? (
                    <div
                      className="uploaded_file"
                      style={{
                        position: "relative",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "150px",
                        border: "1px dashed #D3B88C",
                        borderRadius: "8px",
                      }}
                    >
                      <a
                        href={getSafeUrl(formik.values.document3)}
                        target="_blank"
                        rel="noreferrer"
                        className="file_info"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "8px",
                          width: "100%",
                          textDecoration: "none",
                          color: "inherit",
                          cursor: "pointer",
                        }}
                      >
                        {getSafeUrl(formik.values.document3)
                          .toLowerCase()
                          .match(/\.(pdf|doc|docx)$/) ? (
                          <>
                            <PictureAsPdfIcon
                              sx={{ color: "#D3B88C", fontSize: "30px" }}
                            />
                            <span style={{ fontSize: "12px", wordBreak: "break-all", textAlign: "center", padding: "0 10px" }}>
                              {getSafeUrl(formik.values.document3).split('/').pop()?.split('?')[0]}
                            </span>
                          </>
                        ) : (
                          <img
                            src={getSafeUrl(formik.values.document3)}
                            alt="preview"
                            style={{
                              width: "100%",
                              height: "auto",
                              maxHeight: "150px",
                              objectFit: "cover",
                              borderRadius: "4px",
                            }}
                          />
                        )}
                      </a>
                      {isEditing && (
                        <IconButton
                          size="small"
                          onClick={() =>
                            formik.setFieldValue("document3", "")
                          }
                          sx={{ position: "absolute", top: 5, right: 5 }}
                        >
                          <CloseIcon color="error" />
                        </IconButton>
                      )}
                    </div>
                  ) : (
                    <label
                      className={`upload_action ${!isEditing ? "disabled" : ""}`}
                    >
                      {isEditing && (
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e: any) => {
                            handleAdditionalDocumentUpload(e, "document3");
                            e.target.value = null;
                          }}
                          style={{ display: "none" }}
                        />
                      )}
                      <p>
                        <img
                          src="/static/images/upload_icon.svg"
                          alt="icon"
                        />
                        <strong>Click to upload or drag and drop</strong>
                      </p>
                    </label>
                  )}
                </div>
              </div>
              {isEditing && (
                <div className="form_btn">
                  <Button
                    className="btn btn_primary"
                    variant="contained"
                    type="submit"
                    disabled={isUpdatingProfile}
                  >
                    {isUpdatingProfile ? (user?.sp ? "Updating..." : "Saving...") : (user?.sp ? "Update" : "Save")}
                  </Button>
                </div>
              )}
            </div>
          </form>
        </CustomTabPanel>
      </div>

      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby="otp-modal-title"
      >
        <Box
          className="authBox"
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 480,
            maxWidth: '95%',
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            outline: "none"
          }}
        >
          <IconButton onClick={() => setOpenModal(false)} sx={{ position: "absolute", right: 15, top: 15 }}>
            <CloseIcon />
          </IconButton>
          <h2 id="otp-modal-title" style={{ textAlign: "center", marginBottom: "5px" }}>
            Verify {verificationType === "email" ? "Email" : "Phone"}
          </h2>
          <p style={{ textAlign: "center", marginBottom: "25px" }}>
            A verification OTP has been sent to your{" "}
            {verificationType === "email" ? "email" : "phone number"}. Please
            check and enter it below.
          </p>
          <form className="form">
            <div className="control_group">
              <div className="opt_fields">
                <OTPInput
                  value={otp}
                  onChange={setOtp}
                  numInputs={4}
                  renderInput={(props) => <input {...props} id="otp" />}
                  inputType="tel"
                  shouldAutoFocus
                />
              </div>
              <div style={{ paddingTop: "20px" }} className="auth_bottom">
                <p style={{ textAlign: "center", margin: 0, display: "flex", justifyContent: "center" }}>
                  <button
                    type="button"
                    className="link anchor_link"
                    onClick={() => handleSendVerificationOtp(verificationType!)}
                    disabled={isSendingOtp || timer > 0}
                    style={{ pointerEvents: (isSendingOtp || timer > 0) ? "none" : "auto", opacity: (isSendingOtp || timer > 0) ? 0.6 : 1, border: "none", background: "transparent", fontSize: "14px", fontWeight: 600, color: "#D3B88C", cursor: "pointer" }}
                  >
                    {timer > 0 ? `Resend OTP (${timer}s)` : "Resend OTP"}
                  </button>
                </p>
              </div>
            </div>
            <div className="form_btn">
              <Button
                className="btn btn_primary w_100"
                onClick={handleVerifyOtp}
                disabled={isVerifyingOtp || timer === 0}
              >
                {isVerifyingOtp ? "Verifying..." : "Submit"}
              </Button>
            </div>
          </form>
        </Box>
      </Modal>
    </>
  );
};

export default ManageProfile;
